/**
 * BRUNEL shared protocol - rule precedence (ADR-0003).
 *
 * LOCKED ARCHITECTURE:
 * - Seven fixed authority levels; lower rank = higher authority.
 * - Default deny; denial dominance; worker discretion can never originate a
 *   grant; higher authorities set constraint ceilings.
 */

export const RULE_SCOPE_LEVELS = [
  'system_security',
  'owner',
  'organization',
  'project',
  'repository',
  'mission_task',
  'worker_discretion',
] as const;

export type RuleScopeLevel = (typeof RULE_SCOPE_LEVELS)[number];

export const RULE_SCOPE_RANK: Readonly<Record<RuleScopeLevel, number>> = Object.freeze({
  system_security: 0,
  owner: 1,
  organization: 2,
  project: 3,
  repository: 4,
  mission_task: 5,
  worker_discretion: 6,
});

export type RuleEffect = 'allow' | 'deny';

/** Version of shipped behavior; priority is reserved and does not affect v1. */
export const POLICY_SEMANTICS_VERSION = 'm0-array-order-v1' as const;

export interface PolicySubject {
  readonly orgId?: string;
  readonly projectId?: string;
  readonly repositoryId?: string;
  readonly missionId?: string;
  readonly taskId?: string;
  readonly workerId?: string;
}

export type PolicyConstraintValue = string | number | boolean;

export interface PolicyRule {
  readonly id: string;
  readonly scopeLevel: RuleScopeLevel;
  /** Reserved for a future semantics version; ignored by m0-array-order-v1. */
  readonly priority: number;
  readonly effect: RuleEffect;
  readonly capability: string;
  /** Undefined fields are wildcards; defined fields must match the request. */
  readonly subject?: PolicySubject;
  readonly constraints?: Readonly<Record<string, PolicyConstraintValue>>;
}

export interface PolicyRequest {
  readonly capability: string;
  readonly subject: PolicySubject;
}

export type PolicyDenyReason =
  | 'denial-dominance'
  | 'default-deny'
  | 'worker-discretion-cannot-grant';

export type PolicyDecisionReason = 'granted' | PolicyDenyReason;

export interface PolicyDecision {
  readonly effect: 'allow' | 'deny';
  readonly reason: PolicyDecisionReason;
  readonly determiningLevel: RuleScopeLevel | null;
  readonly matchedRuleIds: readonly string[];
  readonly effectiveConstraints: Readonly<Record<string, PolicyConstraintValue>>;
}

const SUBJECT_FIELDS = [
  'orgId',
  'projectId',
  'repositoryId',
  'missionId',
  'taskId',
  'workerId',
] as const;

function subjectApplies(ruleSubject: PolicySubject | undefined, requestSubject: PolicySubject): boolean {
  if (!ruleSubject) return true;
  for (const field of SUBJECT_FIELDS) {
    const ruleValue = ruleSubject[field];
    if (ruleValue === undefined) continue;
    if (requestSubject[field] !== ruleValue) return false;
  }
  return true;
}

function mergeConstraintCeilings(grants: readonly PolicyRule[]): Record<string, PolicyConstraintValue> {
  const ordered = [...grants].sort(
    (a, b) => RULE_SCOPE_RANK[a.scopeLevel] - RULE_SCOPE_RANK[b.scopeLevel],
  );
  const merged: Record<string, PolicyConstraintValue> = {};
  for (const grant of ordered) {
    if (!grant.constraints) continue;
    for (const [key, value] of Object.entries(grant.constraints)) {
      if (!(key in merged)) merged[key] = value;
    }
  }
  return merged;
}

/**
 * Pure, deterministic policy evaluation (ADR-0003):
 * 1. Default deny.
 * 2. Any applicable deny vetoes, at any level (denial dominance).
 * 3. Worker-discretion allows can never originate a grant.
 * 4. Constraints merge with higher authorities as ceilings (first-set wins).
 */
export function evaluatePolicy(rules: readonly PolicyRule[], request: PolicyRequest): PolicyDecision {
  const applicable = rules.filter(
    (rule) => rule.capability === request.capability && subjectApplies(rule.subject, request.subject),
  );

  const denies = applicable.filter((rule) => rule.effect === 'deny');
  if (denies.length > 0) {
    const strongest = denies.reduce((a, b) =>
      RULE_SCOPE_RANK[a.scopeLevel] <= RULE_SCOPE_RANK[b.scopeLevel] ? a : b,
    );
    return {
      effect: 'deny',
      reason: 'denial-dominance',
      determiningLevel: strongest.scopeLevel,
      matchedRuleIds: applicable.map((rule) => rule.id),
      effectiveConstraints: {},
    };
  }

  const grants = applicable.filter(
    (rule) => rule.effect === 'allow' && rule.scopeLevel !== 'worker_discretion',
  );
  if (grants.length === 0) {
    const discretionOnly = applicable.some((rule) => rule.scopeLevel === 'worker_discretion');
    return {
      effect: 'deny',
      reason: discretionOnly ? 'worker-discretion-cannot-grant' : 'default-deny',
      determiningLevel: null,
      matchedRuleIds: applicable.map((rule) => rule.id),
      effectiveConstraints: {},
    };
  }

  const strongestGrant = grants.reduce((a, b) =>
    RULE_SCOPE_RANK[a.scopeLevel] <= RULE_SCOPE_RANK[b.scopeLevel] ? a : b,
  );
  return {
    effect: 'allow',
    reason: 'granted',
    determiningLevel: strongestGrant.scopeLevel,
    matchedRuleIds: applicable.map((rule) => rule.id),
    effectiveConstraints: mergeConstraintCeilings(grants),
  };
}
